import { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import styled, { css, keyframes, createGlobalStyle } from 'styled-components';
import { Nav as ToolkitNav } from 'toolkit/components/index';
import ContentlyNav from '../../components/common/nav/nav';
import Footer from '../../components/common/footer/index';
import { fetchPortfolioViewData, updateAvailability, markPredictedLanguagesSeen } from './portfolioApi';
import type {
  PortfolioViewData,
  PortfolioClip,
  PortfolioClipPublication,
  FilterOption,
} from './types';

declare global {
  interface Window {
    __PORTFOLIO_USERNAME__?: string;
  }
}

const CONTENTLY_DOMAIN = window.location.hostname.replace(/^[^.]+\./, '');
const mainDomainUrl = (path: string) => `//${CONTENTLY_DOMAIN}${path}`;

// ---------------------------------------------------------------------------
// Palette — matches legacy Contently portfolio CSS
// ---------------------------------------------------------------------------

const C = {
  teal: '#5bc0be',
  tealDark: '#4aafad',
  salmon: '#e8927c',
  textPrimary: '#333',
  textSecondary: '#666',
  textMuted: '#999',
  border: '#e6e8eb',
  bgLight: '#f6f7f8',
  white: '#fff',
  red: '#c0392b',
  greenBadge: '#2a9d8f',
  greenBadgeBg: '#e8f8f7',
};

const CLIP_COLORS = ['#4fc3f7', '#81c784', '#ffb74d', '#e57373', '#ba68c8', '#7986cb', '#a1887f'];

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const PortfolioBodyReset = createGlobalStyle`
  body { margin: 0; }
`;

const Page = styled.div`
  min-height: 100vh;
  background: ${C.white};
`;

/* ---- Notification Banners ---- */

const Banner = styled.div<{ $variant?: 'warning' | 'error' | 'info' }>`
  padding: 12px 20px;
  font-size: 14px;
  color: ${C.textPrimary};
  border-bottom: 1px solid ${C.border};
  background: ${({ $variant }) =>
    $variant === 'error' ? '#fef2f2' : $variant === 'warning' ? '#fffbeb' : '#f0f9ff'};
  text-align: center;
  a { color: ${C.teal}; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const BannerBtn = styled.button<{ $confirm?: boolean }>`
  margin-left: 12px; padding: 4px 14px; border-radius: 3px; cursor: pointer;
  font-size: 12px; text-transform: uppercase; letter-spacing: .5px; font-weight: 500;
  border: 1px solid ${({ $confirm }) => ($confirm ? C.teal : 'transparent')};
  background: ${({ $confirm }) => ($confirm ? 'transparent' : 'transparent')};
  color: ${({ $confirm }) => ($confirm ? C.teal : C.teal)};
  text-decoration: none;
  &:hover { background: ${({ $confirm }) => ($confirm ? C.teal : 'transparent')}; color: ${({ $confirm }) => ($confirm ? C.white : C.tealDark)}; text-decoration: ${({ $confirm }) => ($confirm ? 'none' : 'underline')}; }
`;

const BannerDismiss = styled.button`
  background: none; border: none; cursor: pointer; margin-left: 12px;
  color: ${C.textMuted}; font-size: 16px; vertical-align: middle;
`;

/* ---- Owner Navigation Bar ---- */

const OwnerNav = styled.div`
  background: ${C.bgLight};
  border-bottom: 1px solid ${C.border};
  padding: 10px 0;
`;

const OwnerNavInner = styled.div`
  max-width: 960px; margin: 0 auto; padding: 0 20px;
  display: flex; align-items: center; justify-content: space-between;
`;

const NavLeft = styled.div`display: flex; align-items: center; gap: 12px;`;

const ToggleLabel = styled.label`
  position: relative; display: inline-block; width: 44px; height: 24px; cursor: pointer;
  input { opacity: 0; width: 0; height: 0; }
`;

const Slider = styled.span<{ $on: boolean }>`
  position: absolute; inset: 0; background: ${({ $on }) => ($on ? C.teal : '#ccc')};
  border-radius: 24px; transition: .3s;
  &::before {
    content: ''; position: absolute; height: 18px; width: 18px; left: 3px; bottom: 3px;
    background: ${C.white}; border-radius: 50%; transition: .3s;
    transform: ${({ $on }) => ($on ? 'translateX(20px)' : 'none')};
  }
`;

const NavCenter = styled.div`display: flex; align-items: center; gap: 8px; position: relative;`;

const SmallLabel = styled.span`
  font-size: 11px; text-transform: uppercase; color: ${C.textMuted}; letter-spacing: .5px;
`;

const AvailTooltipWrap = styled.span`
  position: relative; display: inline-flex; align-items: center; cursor: default;
  svg { color: ${C.textMuted}; }
`;

const AvailTooltip = styled.div`
  display: none; position: absolute; bottom: calc(100% + 8px); left: 50%; transform: translateX(-50%);
  background: ${C.textPrimary}; color: ${C.white}; font-size: 12px; padding: 8px 12px;
  border-radius: 4px; white-space: nowrap; z-index: 20; line-height: 1.4;
  ${AvailTooltipWrap}:hover & { display: block; }
`;

const DropBtn = styled.button`
  background: none; border: none; font-size: 13px; color: ${C.textSecondary};
  cursor: pointer; padding: 4px 8px; display: flex; align-items: center; gap: 4px;
  &::after { content: '▾'; font-size: 10px; }
`;

const DropMenu = styled.ul<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  position: absolute; top: 100%; right: 0; background: ${C.white};
  border: 1px solid ${C.border}; border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,.1); list-style: none; padding: 0; margin: 4px 0 0;
  min-width: 240px; z-index: 100;
`;

const DropItem = styled.li<{ $active?: boolean }>`
  padding: 10px 16px; cursor: pointer; font-size: 13px; color: ${C.textPrimary};
  background: ${({ $active }) => ($active ? C.bgLight : 'transparent')};
  &:hover { background: ${C.bgLight}; }
  .sub { font-size: 11px; color: ${C.textMuted}; display: block; margin-top: 2px; }
`;

const OutlineBtn = styled.a`
  display: inline-block; padding: 6px 16px;
  border: 1px solid ${C.teal}; color: ${C.teal};
  font-size: 11px; text-transform: uppercase; letter-spacing: .5px; border-radius: 3px;
  text-decoration: none; font-weight: 500;
  &:hover { background: ${C.teal}; color: ${C.white}; }
`;

/* ---- Profile ---- */

const ContentWrap = styled.div`max-width: 960px; margin: 0 auto; padding: 0 20px;`;

const ProfileSection = styled.div`padding: 40px 0 30px; display: flex; gap: 24px;`;

const AvatarImg = styled.img`
  width: 120px; height: 120px; border-radius: 50%; object-fit: cover; border: 2px solid ${C.border};
`;

const AvatarFallback = styled.div`
  width: 120px; height: 120px; border-radius: 50%; background: ${C.border};
  display: flex; align-items: center; justify-content: center;
  font-size: 40px; color: ${C.textMuted}; border: 2px solid ${C.border};
`;

const ProfileInfo = styled.div`flex: 1;`;

const UserName = styled.h1`
  font-size: 32px; font-weight: 300; color: ${C.textPrimary}; margin: 0 0 8px; line-height: 1.2;
`;

const SocialRow = styled.div`display: flex; gap: 6px; margin-bottom: 10px;`;

const SocialLink = styled.a<{ $inactive?: boolean }>`
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 50%; background: #ccc; color: ${C.white};
  font-size: 13px; text-decoration: none; transition: background .2s;
  ${({ $inactive }) => $inactive && 'opacity: .35;'}
  &:hover { background: #999; }
`;

const MetaRow = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 4px; margin-bottom: 6px;
  font-size: 14px; color: ${C.textSecondary};
`;

const Dot = styled.span`&::before { content: '·'; margin: 0 6px; color: ${C.textMuted}; }`;

const TealLink = styled.a`color: ${C.teal}; text-decoration: none; &:hover { text-decoration: underline; }`;

const DisabledLink = styled.a`color: ${C.textMuted}; text-decoration: none; cursor: default;`;

const Badge = styled.span<{ $pos?: boolean }>`
  display: inline-block; padding: 2px 8px; border-radius: 3px;
  font-size: 10px; text-transform: uppercase; letter-spacing: .5px; font-weight: 600; margin-left: 8px;
  ${({ $pos }) => $pos
    ? `background: ${C.greenBadgeBg}; color: ${C.greenBadge};`
    : `background: #f0f0f0; color: ${C.textMuted};`}
`;

const Headline = styled.h4`font-size: 16px; font-weight: 300; color: ${C.textMuted}; margin: 8px 0 4px;`;

const Bio = styled.p`font-size: 14px; color: ${C.textSecondary}; margin: 0; line-height: 1.5;`;

/* ---- Filters ---- */

const FiltersBar = styled.div`border-top: 1px solid ${C.border}; border-bottom: 1px solid ${C.border}; padding: 12px 0;`;

const FiltersInner = styled.div`
  max-width: 960px; margin: 0 auto; padding: 0 20px;
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
`;

const FilterDrop = styled.div`position: relative;`;

const FilterToggle = styled.button`
  background: none; border: none; font-size: 13px; color: ${C.textSecondary};
  cursor: pointer; padding: 4px 8px;
  &::after { content: '▾'; margin-left: 4px; font-size: 10px; }
`;

const FilterPanel = styled.ul<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  position: absolute; top: 100%; left: 0; background: ${C.white};
  border: 1px solid ${C.border}; border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,.1); list-style: none; padding: 8px 0; margin: 4px 0 0;
  min-width: 220px; max-height: 300px; overflow-y: auto; z-index: 100;
`;

const FilterLi = styled.li<{ $sel?: boolean }>`
  padding: 6px 16px; cursor: pointer; font-size: 13px;
  display: flex; align-items: center; gap: 8px;
  color: ${({ $sel }) => ($sel ? C.teal : C.textPrimary)};
  font-weight: ${({ $sel }) => ($sel ? 600 : 400)};
  &:hover { background: ${C.bgLight}; }
`;

const FilterApply = styled.button`
  display: block; margin: 8px 16px 4px; padding: 6px 16px;
  background: ${C.bgLight}; border: 1px solid ${C.border}; border-radius: 3px;
  font-size: 12px; cursor: pointer; color: ${C.textSecondary};
  &:hover { background: ${C.border}; }
`;

const AppliedFilters = styled.div`display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px;`;

const AppliedTag = styled.span`
  display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px;
  background: ${C.bgLight}; border-radius: 3px; font-size: 12px; color: ${C.textSecondary};
  button { background: none; border: none; cursor: pointer; font-size: 14px; color: ${C.textMuted}; padding: 0; }
`;

const ClearLink = styled.button`
  background: none; border: none; cursor: pointer; font-size: 12px; color: ${C.textMuted};
  text-decoration: underline; padding: 0;
`;

/* ---- Projects ---- */

const ProjectsSection = styled.div`padding: 30px 0 60px;`;

const ProjHeader = styled.div`
  display: flex; align-items: baseline; gap: 12px; margin-bottom: 8px;
`;

const ProjTitle = styled.h4`
  font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px;
  color: ${C.textPrimary}; margin: 0; display: flex; align-items: center; gap: 6px;
`;

const HelpLink = styled.a`
  display: inline-flex; align-items: center; color: ${C.textMuted};
  &:hover { color: ${C.teal}; }
`;

const ProjCount = styled.div`font-size: 13px; color: ${C.textMuted}; margin-bottom: 20px;`;

// True column-packed masonry: a flex row of independent vertical columns.
// `align-items: flex-start` keeps columns from stretching to equal height, and
// each column stacks its own cards — so a short card never inherits the height
// of a taller card in another column (the row-aligned CSS-grid gap regression).
const MasonryGrid = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
`;

const MasonryColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ClipCard = styled.div<{ $bgColor?: string }>`
  border: 1px solid ${C.border}; border-radius: 4px; overflow: hidden;
  transition: box-shadow .2s;
  &:hover { box-shadow: 0 2px 8px rgba(0,0,0,.08); }
  ${({ $bgColor }) => $bgColor && css`background: ${$bgColor};`}
`;

const ClipImgWrap = styled.div`cursor: pointer; position: relative;`;

const ClipImg = styled.img`width: 100%; display: block;`;

const MultiImgBadge = styled.span`
  position: absolute; top: 8px; right: 8px; background: rgba(0,0,0,.55); color: ${C.white};
  border-radius: 3px; padding: 2px 6px; font-size: 11px;
`;

const ClipBody = styled.div`padding: 12px 16px;`;

const ClipActions = styled.div`display: flex; align-items: center; gap: 8px; margin-bottom: 6px;`;

const VisIcon = styled.span<{ $pub: boolean }>`
  position: relative; cursor: default;
  svg { display: block; }
  color: ${({ $pub }) => ($pub ? C.textMuted : C.red)};
`;

const Tooltip = styled.div`
  display: none; position: absolute; bottom: calc(100% + 6px); left: 50%; transform: translateX(-50%);
  background: ${C.textPrimary}; color: ${C.white}; font-size: 11px; white-space: nowrap;
  padding: 4px 8px; border-radius: 3px; z-index: 10;
  ${VisIcon}:hover & { display: block; }
`;

const ViewLink = styled.a`
  font-size: 13px; color: ${C.teal}; text-decoration: none; margin-left: auto;
  &:hover { text-decoration: underline; }
`;

const PubName = styled.h6`
  font-size: 10px; text-transform: uppercase; letter-spacing: .5px; color: ${C.textMuted};
  margin: 0 0 4px; font-weight: 600;
`;

const ClipTitle = styled.h4`
  font-size: 15px; font-weight: 600; color: ${C.textPrimary}; margin: 0; line-height: 1.3;
  a { color: ${C.textPrimary}; text-decoration: none; &:hover { color: ${C.teal}; } }
`;

const ClipDesc = styled.p`
  font-size: 13px; color: ${C.textSecondary}; margin: 6px 0 0; line-height: 1.4;
  /* No line-clamp: classic (legacy _clip.html.haml) rendered the project description in full
     (\`simple_format strip_tags(clip.description)\` with no CSS truncation). The migration added a
     3-line -webkit-line-clamp that classic never had, hiding the full portfolio-specific
     description with no expand/tooltip affordance. Restore legacy parity — show it all.
     eng-maintenance#21169. */
  white-space: pre-line;
`;

/* ---- Infinite Scroll Spinner ---- */

const spin = keyframes`to { transform: rotate(360deg); }`;

const Spinner = styled.div`
  text-align: center; padding: 20px 0;
  &::after {
    content: ''; display: inline-block; width: 24px; height: 24px;
    border: 3px solid ${C.border}; border-top-color: ${C.teal};
    border-radius: 50%; animation: ${spin} .6s linear infinite;
  }
`;

/* ---- Scroll-to-top ---- */

const ScrollTop = styled.button<{ $show: boolean }>`
  position: fixed; bottom: 30px; right: 30px; z-index: 50;
  width: 40px; height: 40px; border-radius: 50%;
  background: ${C.teal}; color: ${C.white}; border: none; cursor: pointer;
  font-size: 18px; box-shadow: 0 2px 8px rgba(0,0,0,.2);
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  pointer-events: ${({ $show }) => ($show ? 'auto' : 'none')};
  transition: opacity .3s;
  &:hover { background: ${C.tealDark}; }
`;

/* ---- Lightbox ---- */

const LightboxOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,.85); z-index: 200;
  display: flex; align-items: center; justify-content: center;
`;

const LightboxImg = styled.img`max-width: 90vw; max-height: 90vh; border-radius: 4px;`;

const LbNav = styled.button`
  position: absolute; top: 50%; transform: translateY(-50%);
  background: rgba(255,255,255,.2); border: none; color: ${C.white};
  font-size: 28px; cursor: pointer; padding: 8px 14px; border-radius: 4px;
  &:hover { background: rgba(255,255,255,.35); }
`;

const LbClose = styled.button`
  position: absolute; top: 16px; right: 16px; background: none; border: none;
  color: ${C.white}; font-size: 28px; cursor: pointer;
`;

/* ---- Footer (matches legacy) ---- */

const PageFooter = styled.footer`border-top: 1px solid ${C.border}; padding: 20px 0;`;

const FooterInner = styled.div`
  max-width: 960px; margin: 0 auto; padding: 0 20px;
  display: flex; justify-content: space-between; align-items: center;
  @media (max-width: 600px) { flex-direction: column; gap: 8px; }
`;

const FooterCopy = styled.span`
  font-size: 11px; text-transform: uppercase; letter-spacing: .5px; color: ${C.textMuted};
`;

const FooterLinks = styled.div`
  display: flex; gap: 16px;
  a { font-size: 13px; color: ${C.teal}; text-decoration: none; &:hover { text-decoration: underline; } }
`;

// ---------------------------------------------------------------------------
// SVG Icons
// ---------------------------------------------------------------------------

const LockSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const UnlockSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 019.9-1" />
  </svg>
);

const QuestionSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const SOCIAL_ICONS: Record<string, string> = {
  facebook: 'M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z',
  twitter: 'M23 3a10.9 10.9 0 01-3.14 1.53A4.48 4.48 0 0012 7.5v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z',
  linkedin: 'M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-4 0v7h-4v-7a6 6 0 016-6zM2 9h4v12H2zM4 2a2 2 0 110 4 2 2 0 010-4z',
  tumblr: 'M14.563 24c-5.093 0-7.031-3.756-7.031-6.411V9.747H5.116V6.648c3.63-1.313 4.512-4.596 4.71-6.469C9.84.09 9.927 0 10.066 0h3.372v6.026h4.594v3.721h-4.608v7.463c.013.994.399 2.358 2.255 2.358h.078c.66-.023 1.545-.217 2.01-.437l1.166 3.498c-.507.805-2.799 1.371-3.86 1.371h-.51z',
  instagram: 'M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 01-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 017.8 2zm-.2 2A3.6 3.6 0 004 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 003.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6zm9.65 1.5a1.25 1.25 0 110 2.5 1.25 1.25 0 010-2.5zM12 7a5 5 0 110 10 5 5 0 010-10zm0 2a3 3 0 100 6 3 3 0 000-6z',
};

function SocialSvg({ provider }: { provider: string }) {
  const d = SOCIAL_ICONS[provider];
  return d ? <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d={d} /></svg> : null;
}

function parseHost(url: string): string {
  try { return new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, ''); }
  catch { return url; }
}

// Responsive masonry column count — mirrors the previous CSS breakpoints
// (3 columns default, 2 at ≤768px, 1 at ≤480px) now that distribution is done
// in JS rather than via `grid-template-columns` media queries.
export function getColumnCount(width: number): number {
  if (width <= 480) return 1;
  if (width <= 768) return 2;
  return 3;
}

// Distribute items across `columnCount` columns round-robin by index. Index-based
// assignment keeps each item in a fixed column as infinite scroll appends more
// items (item i always lands in column i % columnCount), so loading more never
// reflows or reorders already-rendered cards — the property that lets this
// behave like the classic packed-column masonry instead of a row-aligned grid.
export function distributeIntoColumns<T>(items: T[], columnCount: number): T[][] {
  const cols: T[][] = Array.from({ length: Math.max(1, columnCount) }, () => []);
  items.forEach((item, i) => { cols[i % cols.length].push(item); });
  return cols;
}

function shortCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1000000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
  if (n < 1000000000) return `${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}M`;
  return `${(n / 1000000000).toFixed(1)}B`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PortfolioViewPage() {
  const [data, setData] = useState<PortfolioViewData | null>(null);
  const [allClips, setAllClips] = useState<PortfolioClip[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewAsPublic, setViewAsPublic] = useState(false);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [availability, setAvailability] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [columnCount, setColumnCount] = useState(() =>
    typeof window !== 'undefined' ? getColumnCount(window.innerWidth) : 3
  );

  // Pending filter selections (committed on Apply)
  const [pendingPubs, setPendingPubs] = useState<number[]>([]);
  const [pendingTopics, setPendingTopics] = useState<number[]>([]);
  const [pendingSkills, setPendingSkills] = useState<number[]>([]);
  const [pendingFormats, setPendingFormats] = useState<string[]>([]);

  // Committed (applied) filters
  const [appliedPubs, setAppliedPubs] = useState<number[]>([]);
  const [appliedTopics, setAppliedTopics] = useState<number[]>([]);
  const [appliedSkills, setAppliedSkills] = useState<number[]>([]);
  const [appliedFormats, setAppliedFormats] = useState<string[]>([]);

  // Lightbox
  const [lightbox, setLightbox] = useState<{ urls: string[]; idx: number } | null>(null);

  const loadingRef = useRef(false);
  const currentPageRef = useRef(1);
  const totalPagesRef = useRef(1);

  // ---- Data loading ----

  const loadData = useCallback(async (page: number, append = false) => {
    const filters = {
      publicOnly: viewAsPublic || undefined,
      clipPublicationIds: appliedPubs.length ? appliedPubs : undefined,
      topicIds: appliedTopics.length ? appliedTopics : undefined,
      skillIds: appliedSkills.length ? appliedSkills : undefined,
      storyFormats: appliedFormats.length ? appliedFormats : undefined,
    };
    try {
      const result = await fetchPortfolioViewData(page, filters);
      setData(result);
      setAllClips(prev => (append ? [...prev, ...result.clips] : result.clips));
      setCurrentPage(result.pagination.currentPage);
      setTotalPages(result.pagination.totalPages);
      currentPageRef.current = result.pagination.currentPage;
      totalPagesRef.current = result.pagination.totalPages;
      if (!append) setAvailability(result.portfolioUser.availableFreelance);
    } catch (err) {
      console.error('Failed to load portfolio data:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [viewAsPublic, appliedPubs, appliedTopics, appliedSkills, appliedFormats]);

  useEffect(() => { setLoading(true); setAllClips([]); loadData(1); }, [loadData]);

  // ---- Infinite Scroll via window scroll listener (matches legacy near-bottom check) ----

  useEffect(() => {
    if (!data) return;
    const onScroll = () => {
      if (loadingRef.current || currentPageRef.current >= totalPagesRef.current) return;
      const nearBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 600;
      if (nearBottom) {
        loadingRef.current = true;
        setLoadingMore(true);
        loadData(currentPageRef.current + 1, true);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // Fire once on bind — handles "page too short to scroll" case (legacy parity)
    return () => window.removeEventListener('scroll', onScroll);
  }, [data, loadData]);

  // ---- Scroll-to-top visibility ----

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ---- Responsive masonry column count ----

  useEffect(() => {
    const onResize = () => setColumnCount(getColumnCount(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ---- Dropdown dismiss on outside click ----

  useEffect(() => {
    const h = () => { setViewAsOpen(false); setOpenFilter(null); };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);

  // ---- Handlers ----

  const toggleAvailability = async () => {
    const next = !availability;
    setAvailability(next);
    try { await updateAvailability(next); } catch { setAvailability(!next); }
  };

  const applyFilter = (type: string) => {
    if (type === 'clients') setAppliedPubs([...pendingPubs]);
    if (type === 'topics') setAppliedTopics([...pendingTopics]);
    if (type === 'skills') setAppliedSkills([...pendingSkills]);
    if (type === 'formats') setAppliedFormats([...pendingFormats]);
    setOpenFilter(null);
  };

  const clearAllFilters = () => {
    setAppliedPubs([]); setAppliedTopics([]); setAppliedSkills([]); setAppliedFormats([]);
    setPendingPubs([]); setPendingTopics([]); setPendingSkills([]); setPendingFormats([]);
  };

  const removeApplied = (type: string, id: number | string) => {
    if (type === 'pubs') setAppliedPubs(p => p.filter(x => x !== id));
    if (type === 'topics') setAppliedTopics(p => p.filter(x => x !== id));
    if (type === 'skills') setAppliedSkills(p => p.filter(x => x !== id));
    if (type === 'formats') setAppliedFormats(p => p.filter(x => x !== id));
  };

  const togglePending = <T,>(val: T, setter: React.Dispatch<React.SetStateAction<T[]>>) => {
    setter(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);
  };

  // ---- Render helpers ----

  if (loading || !data) {
    return <Page><ContentWrap style={{ padding: '60px 20px', textAlign: 'center', color: C.textMuted }}>Loading portfolio...</ContentWrap></Page>;
  }

  const { portfolioUser: user, isOwner, isAuthenticated, filters, pagination } = data;
  const portfolioUrl = `${user.username}.contently.com`;
  const portfolioFullUrl = `//${user.username}.${CONTENTLY_DOMAIN}/`;
  const hasAnyFilter = appliedPubs.length + appliedTopics.length + appliedSkills.length + appliedFormats.length > 0;
  const hasAnyFilterOption = filters.clipPublications.length + filters.topics.length + filters.skills.length + filters.storyFormats.length > 0;

  const renderFilterDropdown = (
    label: string,
    key: string,
    items: Array<{ id: number | string; name: string; iconUrl?: string | null; faviconUrl?: string | null }>,
    pending: Array<number | string>,
    setPending: React.Dispatch<React.SetStateAction<any[]>>,
  ) => (
    <FilterDrop key={key} onClick={e => e.stopPropagation()}>
      <FilterToggle onClick={() => setOpenFilter(prev => prev === key ? null : key)}>{label}</FilterToggle>
      <FilterPanel $open={openFilter === key}>
        {items.map(item => (
          <FilterLi key={String(item.id)} $sel={pending.includes(item.id)} onClick={() => togglePending(item.id, setPending)}>
            <input type="checkbox" checked={pending.includes(item.id)} readOnly style={{ margin: 0 }} />
            {(item as any).iconUrl && <img src={(item as any).iconUrl} alt="" style={{ width: 20, height: 20, borderRadius: 2 }} />}
            {(item as any).faviconUrl && !(item as any).iconUrl && <img src={(item as any).faviconUrl} alt="" style={{ width: 16, height: 16 }} />}
            {item.name}
          </FilterLi>
        ))}
        <FilterApply onClick={() => applyFilter(key)}>Apply</FilterApply>
      </FilterPanel>
    </FilterDrop>
  );

  // Build applied filter tags with names
  const appliedTags: Array<{ type: string; id: number | string; name: string }> = [];
  appliedPubs.forEach(id => { const f = filters.clipPublications.find(p => p.id === id); if (f) appliedTags.push({ type: 'pubs', id, name: f.name }); });
  appliedTopics.forEach(id => { const f = filters.topics.find(t => t.id === id); if (f) appliedTags.push({ type: 'topics', id, name: f.name }); });
  appliedSkills.forEach(id => { const f = filters.skills.find(s => s.id === id); if (f) appliedTags.push({ type: 'skills', id, name: f.name }); });
  appliedFormats.forEach(id => { const f = filters.storyFormats.find(s => s.id === id); if (f) appliedTags.push({ type: 'formats', id, name: f.name }); });

  const renderClip = (clip: PortfolioClip) => {
    const bgColor = !clip.useImage ? CLIP_COLORS[clip.id % CLIP_COLORS.length] : undefined;
    return (
      <ClipCard key={clip.id} $bgColor={bgColor}>
        {clip.imageUrl && clip.useImage && (
          <ClipImgWrap onClick={() => setLightbox({ urls: clip.imageUrls.length ? clip.imageUrls : [clip.imageUrl!], idx: 0 })}>
            <ClipImg src={clip.imageUrl} alt={clip.title} loading="lazy" />
            {clip.imageUrls.length > 1 && <MultiImgBadge>{clip.imageUrls.length} images</MultiImgBadge>}
          </ClipImgWrap>
        )}
        <ClipBody>
          <ClipActions>
            {isOwner && (
              <VisIcon $pub={clip.public}>
                {clip.public ? <UnlockSvg /> : <LockSvg />}
                <Tooltip>Visible to: {clip.public ? 'Everyone' : 'Contently only'}</Tooltip>
              </VisIcon>
            )}
            <ViewLink href={clip.url} target="_blank" rel="noreferrer nofollow ugc">View ↗</ViewLink>
          </ClipActions>
          {clip.publication && <PubName>{clip.publication.name}</PubName>}
          <ClipTitle><a href={clip.url} target="_blank" rel="noreferrer nofollow ugc">{clip.title}</a></ClipTitle>
          {clip.description && <ClipDesc>{clip.description}</ClipDesc>}
        </ClipBody>
      </ClipCard>
    );
  };

  const clipColumns = distributeIntoColumns(allClips, columnCount);

  return (
    <Page>
      <PortfolioBodyReset />
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{`Stories by ${user.fullName} : Contently`}</title>
        <meta name="description" content={user.bio || ''} />
        <meta name="author" content={user.fullName} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:url" content={window.location.href} />
        <meta property="og:site_name" content="Contently" />
        {user.avatarLargeUrl && <meta property="og:image" content={user.avatarLargeUrl} />}
        {user.avatarLargeUrl && <meta name="twitter:image" content={user.avatarLargeUrl} />}
        <meta property="og:title" content={`Stories by ${user.fullName} : Contently`} />
        <meta name="twitter:title" content={`Stories by ${user.fullName} : Contently`} />
        <meta property="og:description" content={user.headline || ''} />
        <meta name="twitter:description" content={user.headline || ''} />
        <meta property="article:author" content={portfolioFullUrl} />
        <meta name="twitter:site" content="@contently" />
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Global Nav: all authenticated users get ContentlyNav, unauthenticated gets BasicNav */}
      {isAuthenticated && <ContentlyNav />}
      {!isAuthenticated && (
        <ToolkitNav
          basic
          data={{ primaryLinks: [], secondaryLinks: [{ name: 'Get your own', path: mainDomainUrl('/register') }, { name: 'Sign in', path: mainDomainUrl('/signin') }], createLinks: [], userLinks: [] }}
          align="right"
          theme="v1"
        />
      )}

      {/* Notification Banners */}
      {isOwner && user.pendingPortfolio && (
        <Banner $variant="warning">
          You're awaiting approval from our Creative Marketplace Team.{' '}
          <a href="https://contently-support.kayako.com/article/927-optimizing-existing-content-for-organic-search-visibility" target="_blank" rel="noopener noreferrer">Learn More</a> about optimizing your portfolio.
        </Banner>
      )}
      {isOwner && user.declinedPortfolio && (
        <Banner $variant="error">
          Your portfolio is currently private. Learn why and the steps to make it public{' '}
          <a href="https://contently-support.kayako.com/article/832-portfolio-visibility-private-vs-public" target="_blank" rel="noopener noreferrer">here</a>.
        </Banner>
      )}
      {isOwner && user.displayPredictionBanner && !bannerDismissed && (
        <Banner $variant="info">
          <span dangerouslySetInnerHTML={{ __html: `Hi ${user.fullName}! Based on your portfolio's clips it looks like you're fluent in ${user.predictionLanguages}.` }} />
          <BannerBtn onClick={(e) => { e.preventDefault(); const editUrl = mainDomainUrl('/portfolio/edit?from_prediction_banner=true'); markPredictedLanguagesSeen().then(() => { window.location.href = editUrl; }).catch(() => { window.location.href = editUrl; }); }}>No, let me edit</BannerBtn>
          <BannerBtn $confirm onClick={() => { setBannerDismissed(true); markPredictedLanguagesSeen().catch(() => {}); }}>Confirm</BannerBtn>
          <BannerDismiss onClick={() => { setBannerDismissed(true); markPredictedLanguagesSeen().catch(() => {}); }}>✕</BannerDismiss>
        </Banner>
      )}

      {/* Owner Navigation Bar */}
      {isOwner && (
        <OwnerNav>
          <OwnerNavInner>
            <NavLeft>
              {user.publicPortfolio && (
                <>
                  <ToggleLabel>
                    <input type="checkbox" checked={availability} onChange={toggleAvailability} />
                    <Slider $on={availability} />
                  </ToggleLabel>
                  <SmallLabel>Available to work</SmallLabel>
                  <AvailTooltipWrap>
                    <QuestionSvg />
                    <AvailTooltip>Let brands know that you are currently available for freelance work.</AvailTooltip>
                  </AvailTooltipWrap>
                </>
              )}
            </NavLeft>
            <NavCenter onClick={e => e.stopPropagation()}>
              {user.publicPortfolio && (
                <>
                  <SmallLabel>View as:</SmallLabel>
                  <div style={{ position: 'relative' }}>
                    <DropBtn onClick={() => setViewAsOpen(v => !v)}>
                      {viewAsPublic ? 'A Public Site' : 'A Contently Brand'}
                    </DropBtn>
                    <DropMenu $open={viewAsOpen}>
                      <DropItem $active={!viewAsPublic} onClick={() => { setViewAsPublic(false); setViewAsOpen(false); }}>
                        A Contently Brand<span className="sub">View as a Contently Brand</span>
                      </DropItem>
                      <DropItem $active={viewAsPublic} onClick={() => { setViewAsPublic(true); setViewAsOpen(false); }}>
                        A Public Site<span className="sub">View as your personal live site</span>
                      </DropItem>
                    </DropMenu>
                  </div>
                </>
              )}
            </NavCenter>
            <div>
              <OutlineBtn href={mainDomainUrl('/portfolio/edit?from_portfolio=true')}>Edit Portfolio</OutlineBtn>
            </div>
          </OwnerNavInner>
        </OwnerNav>
      )}

      {/* Profile Section */}
      <ContentWrap>
        <ProfileSection>
          <div style={{ flexShrink: 0 }}>
            {user.avatarUrl
              ? <AvatarImg src={user.avatarUrl} alt={user.fullName} />
              : <AvatarFallback>{user.firstName?.[0]}{user.lastName?.[0]}</AvatarFallback>}
          </div>
          <ProfileInfo>
            <UserName>{user.fullName}</UserName>
            {(isOwner || user.hasSocialLink) && user.socialLink && (
              <SocialRow>
                {(['facebook', 'twitter', 'linkedin', 'tumblr', 'instagram'] as const).map(p => {
                  const urlKey = `${p}Url` as keyof typeof user.socialLink;
                  const url = user.socialLink?.[urlKey];
                  if (!url && !isOwner) return null;
                  return isOwner && !url
                    ? <SocialLink key={p} href={mainDomainUrl('/account/social_links/edit')} target="_blank" $inactive><SocialSvg provider={p} /></SocialLink>
                    : <SocialLink key={p} href={url!} target="_blank" rel="noopener noreferrer"><SocialSvg provider={p} /></SocialLink>;
                })}
              </SocialRow>
            )}
            <MetaRow>
              {user.location && <span>{user.location}</span>}
              {user.websiteUrlValid && user.websiteUrl && (
                <>{user.location && <Dot />}<TealLink href={user.websiteUrl} target="_blank" rel="noopener noreferrer">{parseHost(user.websiteUrl).substring(0, 31)}</TealLink></>
              )}
              <>{(user.location || user.websiteUrlValid) && <Dot />}
                {user.publicPortfolio
                  ? <TealLink href={portfolioFullUrl} target="_blank" rel="nofollow ugc">{portfolioUrl}</TealLink>
                  : <DisabledLink href={portfolioFullUrl} rel="nofollow ugc">{portfolioUrl}</DisabledLink>}
                {isOwner && user.publicPortfolio && <Badge $pos>Website Live</Badge>}
                {user.pendingPortfolio && <Badge>Pending Approval</Badge>}
              </>
            </MetaRow>
            {user.headline && <Headline>{user.headline}</Headline>}
            {user.bio && <Bio>{user.bio}</Bio>}
          </ProfileInfo>
        </ProfileSection>
      </ContentWrap>

      {/* Filters */}
      {hasAnyFilterOption && (
        <FiltersBar>
          <FiltersInner>
            <SmallLabel>Filter by</SmallLabel>
            {filters.clipPublications.length > 0 && renderFilterDropdown('Clients', 'clients', filters.clipPublications, pendingPubs, setPendingPubs)}
            {filters.topics.length > 0 && renderFilterDropdown('Topics', 'topics', filters.topics, pendingTopics, setPendingTopics)}
            {filters.skills.length > 0 && renderFilterDropdown('Skills', 'skills', filters.skills, pendingSkills, setPendingSkills)}
            {filters.storyFormats.length > 0 && renderFilterDropdown('Formats', 'formats', filters.storyFormats, pendingFormats, setPendingFormats)}
            {hasAnyFilter && <ClearLink onClick={clearAllFilters}>Clear all</ClearLink>}
          </FiltersInner>
          {appliedTags.length > 0 && (
            <ContentWrap style={{ paddingTop: 8 }}>
              <AppliedFilters>
                {appliedTags.map(t => (
                  <AppliedTag key={`${t.type}-${t.id}`}>
                    {t.name}
                    <button onClick={() => removeApplied(t.type, t.id)}>✕</button>
                  </AppliedTag>
                ))}
              </AppliedFilters>
            </ContentWrap>
          )}
        </FiltersBar>
      )}

      {/* Projects */}
      <ContentWrap>
        <ProjectsSection>
          <ProjHeader>
            <ProjTitle>
              Projects
              <HelpLink href="https://contently-support.kayako.com/article/970-setting-up-your-portfolio" target="_blank" rel="noopener noreferrer"><QuestionSvg /></HelpLink>
            </ProjTitle>
            {isOwner && <TealLink href={mainDomainUrl('/portfolio/projects?from_portfolio=true')}>Edit or add projects</TealLink>}
          </ProjHeader>
          <ProjCount>
            {shortCount(pagination.totalCount)} project{pagination.totalCount !== 1 ? 's' : ''} for {shortCount(filters.clipPublicationsCount)} client{filters.clipPublicationsCount !== 1 ? 's' : ''}
          </ProjCount>

          <MasonryGrid>
            {clipColumns.map((column, colIdx) => (
              <MasonryColumn key={colIdx}>
                {column.map(renderClip)}
              </MasonryColumn>
            ))}
          </MasonryGrid>

          {loadingMore && <Spinner />}
        </ProjectsSection>
      </ContentWrap>

      {/* Footer */}
      {isAuthenticated ? (
        <Footer />
      ) : (
        <PageFooter>
          <FooterInner>
            <FooterCopy>&copy; {new Date().getFullYear()} Contently</FooterCopy>
            <FooterLinks>
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              <a href="https://support.contently.com/" target="_blank" rel="noopener noreferrer">Help</a>
            </FooterLinks>
          </FooterInner>
        </PageFooter>
      )}

      {/* Scroll-to-top */}
      <ScrollTop $show={showScrollTop} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>↑</ScrollTop>

      {/* Lightbox */}
      {lightbox && (
        <LightboxOverlay onClick={() => setLightbox(null)}>
          <LbClose onClick={() => setLightbox(null)}>✕</LbClose>
          {lightbox.urls.length > 1 && (
            <LbNav style={{ left: 16 }} onClick={e => { e.stopPropagation(); setLightbox(prev => prev && ({ ...prev, idx: (prev.idx - 1 + prev.urls.length) % prev.urls.length })); }}>‹</LbNav>
          )}
          <LightboxImg src={lightbox.urls[lightbox.idx]} alt="" onClick={e => e.stopPropagation()} />
          {lightbox.urls.length > 1 && (
            <LbNav style={{ right: 16 }} onClick={e => { e.stopPropagation(); setLightbox(prev => prev && ({ ...prev, idx: (prev.idx + 1) % prev.urls.length })); }}>›</LbNav>
          )}
        </LightboxOverlay>
      )}
    </Page>
  );
}
